import {ofType} from 'redux-observable';
import {of, from, merge} from 'rxjs';
import {flatMap, map, catchError} from 'rxjs/operators';
import i18next from 'i18next';
import {initialize, reset} from 'redux-form';

import {combineEpics} from 'web-app/util/redux-observable';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import {showDialog} from 'web-app/react/dialog';
import {updateAccountDetailsObservable} from 'web-app/react/account-settings/account-details/epics';
import * as AccountSelectors from 'web-app/react/session/account/selectors';
import {type RootState} from 'web-app/react/redux/root-state';

import * as API from './api';
import * as Actions from './actions';
import * as Constants from './constants';
import {type FormData} from './types';

const fetch = action$ =>
    action$.pipe(
        ofType(Actions.fetch.type),
        flatMap(() =>
            API.fetch().pipe(
                flatMap(({formData, show}) =>
                    merge(of(Actions.fetchSuccess.action({formData, show})), fetchTimezonesObservable()),
                ),
                catchError(e => of(Actions.fetchFailed.action(e))),
            ),
        ),
    );

const fetchTimezonesObservable = () =>
    API.fetchTimezones().pipe(
        map(timezones => Actions.fetchTimezonesSuccess.action(timezones)),
        catchError(e => of(Actions.fetchTimezonesFailed.action(e))),
    );

const fetchTimezones = action$ => action$.pipe(ofType(Actions.fetchTimezones.type), flatMap(fetchTimezonesObservable));

const getUpdateObservable = (formData: FormData, isEmployee: boolean, timezone: string) =>
    API.update(formData).pipe(
        flatMap(formData => [
            Actions.updateSuccess.action(formData),
            GlobalEventActions.updateSuccess.action(
                i18next.t(
                    (formData.active === 'enabled' || (formData.active === 'disabled' && formData.disableWeekends)) &&
                        isEmployee
                        ? 'account.notificationHoursSettings.successPositive'
                        : 'account.notificationHoursSettings.success',
                ),
            ),
            initialize(Constants.FORM, {...formData, timezone}, false),
        ]),
        catchError(e => of(Actions.updateFailed.action(e))),
    );

const updateTimezone = (state: RootState, timezone: string) => {
    const account = AccountSelectors.getAccount(state);
    return updateAccountDetailsObservable({
        firstName: account.firstName,
        middleName: account.middleName,
        lastName: account.lastName,
        street: account.street,
        zip: account.zip,
        city: account.city,
        phone: account.phone,
        homePhone: account.homePhone,
        workPhone: account.workPhone,
        locale: account.locale,
        timezone,
    });
};

const update = (action$, state$) =>
    action$.pipe(
        ofType(Actions.update.type),
        flatMap(({payload}: ReturnType<typeof Actions.update.action>) => {
            if (payload.isEmployee && payload.formData.active === 'disabled' && !payload.formData.disableWeekends) {
                return from(
                    showDialog({
                        title: i18next.t('account.notificationHoursSettings.employeeConfirmAlertsTitle'),
                        description: i18next.t('account.notificationHoursSettings.employeeConfirmAlertsBody'),
                        /*
                            ok and cancel is actually flipped around here, since we emphasize on "cancelling"
                            turning on your notifications in the weekends and outside work hours
                         */
                        okText: i18next.t('account.notificationHoursSettings.employeeConfirmAlertsDismiss'),
                        cancelText: i18next.t('account.notificationHoursSettings.employeeConfirmAlertsConfirm'),
                    }),
                ).pipe(
                    flatMap(choice => {
                        if (choice === 'CANCEL') {
                            return merge(
                                getUpdateObservable(payload.formData, payload.isEmployee, payload.formData.timezone),
                                updateTimezone(state$.value, payload.formData.timezone),
                            );
                        }
                        return merge(of(Actions.updateAborted.action()), of(reset(Constants.FORM)));
                    }),
                );
            }
            return merge(
                getUpdateObservable(payload.formData, payload.isEmployee, payload.formData.timezone),
                updateTimezone(state$.value, payload.formData.timezone),
            );
        }),
    );

export default combineEpics(fetch, update, fetchTimezones, fetchTimezones);
